import React from 'react';
import FeeTiersEditor from './FeeTiersEditor';
import { feeSummary } from './feeTiers';

// Order By option add / edit form, grouped so a buyer is readable at a glance.
// Same change contract as the old flat AddEditForm (handleInputChange with
// name / value / type / checked) plus onTiersChange for the fee rows.

const input = 'px-3 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6';

export const ORDERER_TYPES = [
  { value: '', label: 'ไม่ระบุ' },
  { value: 'own', label: 'own — พนักงาน (เช่น Phat)' },
  { value: 'external', label: 'external — ฝากภายนอก (เช่น Eve, Wan, Big)' },
  { value: 'mercariAccount', label: 'mercariAccount — บัญชี Mercari2-5' },
];

const GROUPS = [
  {
    title: 'Basic',
    open: () => true,
    fields: [
      { title: 'Value', name: 'value', type: 'text' },
      { title: 'Label', name: 'label', type: 'text' },
      { title: 'Sort order', name: 'sortOrder', type: 'number', step: '1' },
      { title: 'Orderer type (routing)', name: 'orderByType', type: 'select', options: ORDERER_TYPES },
      { title: 'Active', name: 'isActive', type: 'boolean' },
    ],
  },
  { title: 'Fee', open: () => true, fee: true },
  {
    title: 'Daily limits',
    open: (d) => ['dailyOrderLimit', 'dailyAmountLimitJpy', 'extendedDailyAmountLimitJpy', 'limitExtendHourThai'].some((k) => d[k] !== '' && d[k] != null),
    fields: [
      { title: 'Orders per day (ว่าง = ไม่จำกัด)', name: 'dailyOrderLimit', type: 'number', step: '1' },
      { title: 'Amount per day ¥ (ว่าง = ไม่จำกัด)', name: 'dailyAmountLimitJpy', type: 'number', step: '1' },
      { title: 'Strict amount cap (ปิด = soft cap)', name: 'isAmountLimitStrict', type: 'boolean' },
      { title: 'Extended limit ¥ (หลังเวลาที่กำหนด)', name: 'extendedDailyAmountLimitJpy', type: 'number', step: '1' },
      { title: 'Unlock hour Thai (เช่น 20)', name: 'limitExtendHourThai', type: 'number', step: '1' },
      { title: 'Carry-over balance ¥ (Merpay เหลือจากเมื่อวาน)', name: 'carryOverBalanceJpy', type: 'number', step: '1000' },
    ],
  },
  {
    title: 'LINE',
    open: (d) => Boolean(d.lineGroupId || d.contactInfo || d.dispatchLineId),
    fields: [
      { title: 'Buyer LINE — Chatcone socialId (dispatch bot sends job cards here, 1-to-1)', name: 'dispatchLineId', type: 'text' },
      { title: 'LINE group ID (จาก LINE bot webhook)', name: 'lineGroupId', type: 'text' },
      { title: 'LINE group name', name: 'lineGroupName', type: 'text' },
      { title: 'Contact info (LINE ID / group)', name: 'contactInfo', type: 'text' },
      { title: 'Smile name required (ปิด = ไม่ต้องใส่ smile name — admin จะถูกแจ้ง)', name: 'isSmileNameInputRequire', type: 'boolean' },
    ],
  },
  {
    title: 'Google Sheet',
    open: (d) => Boolean(d.googleSheetId || d.dispatchSheetId || d.balanceSheetId),
    fields: [
      { title: 'Dispatch bot sheet ID (bot orders only — the buyer\'s own sheet, shared with them)', name: 'dispatchSheetId', type: 'text' },
      { title: 'Dispatch bot sheet tab', name: 'dispatchSheetTab', type: 'text' },
      { title: 'Balance sheet ID (only when ยอดคงเหลือ is not on the sheets here — read-only, for สรุปคนกด)', name: 'balanceSheetId', type: 'text' },
      { title: 'Balance sheet tab', name: 'balanceSheetTab', type: 'text' },
      { title: 'Sheet ID (ปลายทาง append order หลัง confirm)', name: 'googleSheetId', type: 'text' },
      { title: 'Sheet tab', name: 'googleSheetTab', type: 'text' },
      {
        title: 'Sheet source',
        name: 'sheetSource',
        type: 'select',
        options: [
          { value: 'line', label: 'line — warehouse + shop จาก LINE group (A:E)' },
          { value: 'order', label: 'order — ใช้ Order.shop ตรง ๆ (A:D)' },
        ],
      },
      { title: 'Require LINE group link (ต้องเจอลิงก์ใน LINE ก่อน push)', name: 'requireLineGroupLink', type: 'boolean' },
      { title: 'Tracking sync enabled (ดึงเลข tracking กลับทุกชั่วโมง)', name: 'trackingSyncEnabled', type: 'boolean' },
      { title: 'Tracking URL column (เช่น B)', name: 'trackingUrlColumn', type: 'text' },
      { title: 'Tracking column (เช่น H)', name: 'trackingColumn', type: 'text' },
      { title: 'Tracking data start row (เช่น 2)', name: 'trackingDataStartRow', type: 'number', step: '1' },
    ],
  },
  {
    title: 'Auction',
    open: (d) => Boolean(d.isAuctionAccount || d.mercariUserId),
    fields: [
      { title: 'Auction account (รับซื้อ Mercari Auction)', name: 'isAuctionAccount', type: 'boolean' },
      { title: 'Auction for shop (smile / tama / ว่าง = ทุก shop)', name: 'auctionForShop', type: 'text' },
      { title: 'Mercari user ID (jp.mercari.com/user/profile/<id>)', name: 'mercariUserId', type: 'text' },
    ],
  },
  {
    title: 'Old routing (to be removed)',
    open: (d) => Boolean(d.isUrgencyAnchor),
    fields: [
      { title: 'Urgency anchor', name: 'isUrgencyAnchor', type: 'boolean' },
    ],
  },
];

const Field = ({ f, data, onChange }) => (
  <div className={f.type === 'boolean' ? 'flex items-center gap-2 pt-5' : ''}>
    {f.type === 'boolean' ? (
      <>
        <input id={f.name} name={f.name} type="checkbox" checked={!!data[f.name]} onChange={onChange} className="rounded text-indigo-600 focus:ring-indigo-500" />
        <label htmlFor={f.name} className="text-sm text-gray-900">{f.title}</label>
      </>
    ) : (
      <>
        <label htmlFor={f.name} className="block text-sm font-medium leading-6 text-gray-900">{f.title}</label>
        {f.type === 'select' ? (
          <select id={f.name} name={f.name} value={data[f.name] ?? ''} onChange={onChange} className={`${input} py-2`}>
            {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : (
          <input id={f.name} name={f.name} type={f.type} step={f.step} value={data[f.name] ?? ''} onChange={onChange} className={input} />
        )}
      </>
    )}
  </div>
);

export default function OrderByOptionForm({
  name, data, mode, closeForm, handleInputChange, handleRegister, handleUpdate, onTiersChange, onSpecialTiersChange, submitting,
}) {
  const isEdit = mode === 'edit';
  const submit = (e) => {
    e.preventDefault();
    if (submitting) return;
    (isEdit ? handleUpdate : handleRegister)();
  };
  const old = feeSummary({ ...data, feeTiers: null });

  return (
    <form onSubmit={submit} className="px-2 py-4 space-y-3">
      <h2 className="text-center text-2xl font-bold text-gray-900">{name}</h2>
      {GROUPS.map((g) => (
        <details key={g.title} open={g.open(data)} className="rounded-md border border-gray-200 px-4 py-2">
          <summary className="cursor-pointer select-none text-sm font-semibold text-gray-800">{g.title}</summary>
          {g.fee ? (
            <div className="mt-3 space-y-2">
              <FeeTiersEditor rows={data.feeTierRows} onChange={onTiersChange} />
              <p className="pt-2 text-sm font-semibold text-gray-800">Special fee tiers (ต่อราคา + ประมูล)</p>
              <p className="text-xs text-gray-500">Optional second price list. The buyer's bot sheet gets a พิเศษ checkbox per row; ticked = this list applies. Leave empty for no special price.</p>
              <FeeTiersEditor rows={data.specialFeeTierRows || []} onChange={onSpecialTiersChange} />
              {old.text !== 'none' && (
                <p className="text-xs text-orange-600">
                  Old fee fields on this buyer: {old.text.replace('old: ', '')}. They stop being used once tiers are saved (and are removed later).
                </p>
              )}
            </div>
          ) : (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
              {g.fields.map((f) => <Field key={f.name} f={f} data={data} onChange={handleInputChange} />)}
            </div>
          )}
        </details>
      ))}
      <div className="flex gap-4 pt-2">
        <button type="submit" disabled={submitting} className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-800 disabled:opacity-50">
          {isEdit ? 'Update' : 'Register'}
        </button>
        <button type="button" onClick={closeForm} className="flex w-full justify-center rounded-md bg-gray-400 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-gray-600">
          Cancel
        </button>
      </div>
    </form>
  );
}
